import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { TabContext } from './TabContext';
import { StyledTabs, StyledTabsBorder, StyledTabsContainer } from './Tabs.styled';
/**
 * The Tabs component renders a list of tabs, and controls which tab's content
 * is rendered.
 *
 * @example
 * ```typescript
 * enum TabNames {
 * 	recents,
 * 	favorites,
 * }
 *
 * function MyTabs() {
 * 	const [activeTab, setActiveTab] = React.useState<string | number>(TabNames.recents);
 * 	return (
 * 		<Tabs onChange={setActiveTab} activeTab={activeTab}>
 * 			{tabContext =>
 * 				<>
 * 					<Tab label="Recents" value={TabNames.recents} tabContext={tabContext}>
 * 						Recents tab content
 * 					</Tab>
 * 					<Tab label="Favorites" value={TabNames.favorites} tabContext={tabContext}>
 * 						Favorites tab content
 * 					</Tab>
 * 				</>
 * 			}
 * 		</Tabs>
 * 	);
 * };
 * ```
 */
export function Tabs(props) {
  var _props$contentPortal, _props$contentPortal2;
  var tabListRef = React.useRef(null);
  var tabContainerRef = React.useRef(null);
  var contentIdRef = React.useRef("flow-web-tab-content-".concat(Math.random().toString().replace('.', '-')) // no decimals in query selectors
  );
  var _React$useState = React.useState({}),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    tabs = _React$useState2[0],
    setTabs = _React$useState2[1];
  var _React$useState3 = React.useState(),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    tabObservers = _React$useState4[0],
    setTabObservers = _React$useState4[1];
  var observer = React.useMemo(function () {
    return new ResizeObserver(function (entries) {
      setTabObservers(entries);
    });
  }, []);
  React.useEffect(function () {
    setScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, props.activeTab, tabObservers]);
  var tabEntries = Object.entries(tabs);
  var currentTab = props.activeTab != null ? tabs[tabKey(props.activeTab)] : undefined;
  var _props$tabsRenderer = props.tabsRenderer,
    tabsRenderer = _props$tabsRenderer === void 0 ? function (tabslist) {
      return tabslist;
    } : _props$tabsRenderer;
  React.useEffect(function () {
    if (props.activeTab == null && tabEntries.length) {
      props.onChange(tabEntries[0][1].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeTab, tabs]);
  var tabContext = {
    contentId: contentIdRef.current,
    fullWidth: props.fullWidth,
    activeTab: props.activeTab,
    observer: observer,
    registerTab: function registerTab(value, tab) {
      if (tabs[tabKey(value)]) {
        return false;
      }
      setTabs(function (_ref) {
        var currTabs = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
        currTabs[tabKey(value)] = _objectSpread(_objectSpread({}, tab), {}, {
          value: value
        });
        return currTabs;
      });
      return true;
    },
    unregisterTab: function unregisterTab(value) {
      setTabs(function (_ref2) {
        var currTabs = _extends({}, (_objectDestructuringEmpty(_ref2), _ref2));
        delete currTabs[tabKey(value)];
        return currTabs;
      });
    },
    changeTab: props.onChange,
    isLastTab: function isLastTab(value) {
      return props.tabsRenderer ? false : tabEntries.findIndex(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 1),
          key = _ref4[0];
        return key === tabKey(value);
      }) === tabEntries.length - 1;
    }
  };
  var portalProps = {
    role: 'tabpanel',
    id: contentIdRef.current
  };
  if (currentTab) {
    portalProps['aria-labelledby'] = currentTab.tabId;
  }
  return /*#__PURE__*/React.createElement(TabContext.Provider, {
    value: tabContext
  }, /*#__PURE__*/React.createElement(StyledTabsContainer, {
    style: props.style,
    className: props.className,
    ref: tabContainerRef
  }, tabsRenderer(/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledTabs, {
    ref: tabListRef,
    role: "tablist"
  }, typeof props.children === 'function' ? props.children(tabContext) : props.children))), /*#__PURE__*/React.createElement(StyledTabsBorder, null)), (_props$contentPortal = (_props$contentPortal2 = props.contentPortal) === null || _props$contentPortal2 === void 0 ? void 0 : _props$contentPortal2.call(props, portalProps)) !== null && _props$contentPortal !== void 0 ? _props$contentPortal : /*#__PURE__*/React.createElement("section", _extends({}, portalProps, {
    className: currentTab === null || currentTab === void 0 ? void 0 : currentTab.className
  })));
  function setScroll() {
    if (!tabListRef.current || !tabContainerRef.current) {
      return;
    }
    var activeTabEl = getActiveTabEl();
    if (activeTabEl) {
      tabContainerRef.current.scrollLeft = getIndicatorOffsetLeft(activeTabEl, tabListRef.current);
    }
  }
  function getActiveTabEl() {
    var _tabListRef$current;
    var activeTab = props.activeTab;
    if (activeTab == null) {
      return undefined;
    }
    var activeTabIndex = tabEntries.findIndex(function (_ref5) {
      var _ref6 = _slicedToArray(_ref5, 1),
        value = _ref6[0];
      return value === tabKey(activeTab);
    });
    var item = (_tabListRef$current = tabListRef.current) === null || _tabListRef$current === void 0 ? void 0 : _tabListRef$current.children.item(activeTabIndex);
    return item ? item : item;
  }
  function getIndicatorOffsetLeft(activeTabEl, tabsListEl) {
    var activeTabElRect = activeTabEl.getBoundingClientRect();
    var tabListRect = tabsListEl.getBoundingClientRect();
    return activeTabElRect.left - tabListRect.left;
  }
}
function tabKey(value) {
  return "tab-".concat(value, "-key");
}